import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';

import {
  AbstractControl,
  UntypedFormBuilder,
  UntypedFormGroup,
  ValidationErrors,
} from '@angular/forms';
import { Router } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';
import { EnumErrorStates, ErrorObject } from '@inmoment/the-kitchen';

import {
  Mode,
  Scale,
} from 'app/components/uikit/lex-horizontal-stepper/lex-horizontal-stepper.component';
import { Constants } from 'app/constants/constants';
import { Dashboard } from 'app/providers/bridge-api/dashboard.service';
import { GroupService } from 'app/providers/semantria-api/group.service';
import { ActionPlan, ActionPlanService } from 'app/providers/storage-api/actionplan.service';
import { CalculationRequestProperties } from 'app/providers/storage-api/predictive.service';
import { ProjectService } from 'app/providers/storage-api/project.service';
import {
  injectComponentTelemetryService,
  EnableComponentTelemetry,
  EnableClassTelemetry,
} from 'app/services/otel.service';
import { Utils } from 'app/utils/utils';
import { WidgetDataSource } from '../../../../models';
import { AlertService } from '../../../../services/alert.service';
import { Driver } from '../../../widget/models/predictive.models';

@EnableClassTelemetry()
@Component({
  selector: 'app-create-action-plan-dialog',
  templateUrl: './create-action-plan-dialog.component.html',
  styleUrls: ['./create-action-plan-dialog.component.scss'],
  standalone: false,
})
export class CreateActionPlanDialogComponent implements OnInit {
  @EnableComponentTelemetry() otelService = injectComponentTelemetryService();
  @Input() driver: Driver = null;
  @Input() actionPlan: ActionPlan = null;
  @Input() selectedBreakdown = '';
  @Input() breakdownColumn: string;
  @Input() scale: Scale;
  @Input() widgetDataSource: WidgetDataSource;
  @Input() dashboard: Dashboard;
  @Input() scoringStrategy: 'AVERAGE' | 'BOX' | 'NPS';
  @Input() analysisMetadata: CalculationRequestProperties;

  @Output() create = new EventEmitter<ActionPlan>();
  @Output() update = new EventEmitter<ActionPlan>();
  @Output() modalClosed = new EventEmitter<void>();

  public titleLabel: string;
  public saveLabel: string;
  public actionLabel: string;
  public currentStep = 1;
  public isSaving = false;
  public form: UntypedFormGroup;
  public actionPlanMinDueDate: Date;
  public dueDateStatus: ErrorObject = { state: EnumErrorStates.SUCCESS };
  public simulatedValue = 0;
  public currentScoringStrategyText: string;
  public targetScoringStrategyText: string;
  stepperMode: Mode;

  constructor(
    private alertService: AlertService,
    private actionPlanService: ActionPlanService,
    private groupService: GroupService,
    private projectService: ProjectService,
    private router: Router,
    private translocoService: TranslocoService,
    formBuilder: UntypedFormBuilder
  ) {
    this.form = formBuilder.group({
      actionPlanNote: [''],
      actionPlanDueDate: ['', this.dueDateValidator.bind(this)],
    });

    this.actionPlanMinDueDate = new Date();
    this.actionPlanMinDueDate.setDate(this.actionPlanMinDueDate.getDate() - 1);
  }

  public ngOnInit(): void {
    this.simulatedValue = this.driver.simulated;

    if (this.actionPlan) {
      this.titleLabel = this.translocoService.translate(
        'create-action-plan-dialog-component.edit-action-plan'
      );
      this.saveLabel = this.translocoService.translate(
        'create-action-plan-dialog-component.save-action-plan'
      );
      this.actionLabel = this.translocoService.translate(
        'create-action-plan-dialog-component.saved'
      );

      this.form.patchValue({
        actionPlanNote: this.actionPlan.notes,
        actionPlanDueDate: new Date(this.actionPlan.due_date),
      });
      this.simulatedValue = this.actionPlan.metric_target_value;
    } else {
      this.titleLabel = this.translocoService.translate(
        'create-action-plan-dialog-component.create-action-plan'
      );
      this.saveLabel = this.translocoService.translate(
        'create-action-plan-dialog-component.create-action-plan'
      );
      this.actionLabel = this.translocoService.translate(
        'create-action-plan-dialog-component.created'
      );
    }

    this.currentScoringStrategyText = this.getScoringStrategyText(this.scoringStrategy);
    this.targetScoringStrategyText = this.actionPlan
      ? this.getScoringStrategyText(
          this.actionPlan.metadata.scoringStrategy as 'AVERAGE' | 'BOX' | 'NPS'
        )
      : this.currentScoringStrategyText;
  }

  private getScoringStrategyText(scoringStrategy: 'AVERAGE' | 'BOX' | 'NPS'): string {
    switch (scoringStrategy) {
      default:
      case 'AVERAGE':
        return this.translocoService.translate('create-action-plan-dialog-component.avg');
      case 'BOX':
        return this.translocoService.translate('create-action-plan-dialog-component.topbox');
      case 'NPS':
        return this.translocoService.translate('create-action-plan-dialog-component.nps');
    }
  }

  public onCancel() {
    this.modalClosed.emit();
  }

  public onBack() {
    this.modalClosed.emit();
  }

  public onGoToPlan(): void {
    this.router.navigate([
      `/groups/${this.groupService.getSelectedGroupId(true)}/action-plans/${
        this.actionPlan.plan_id
      }`,
    ]);
  }

  public onDueDateFocusOut(): void {
    this.form.controls.actionPlanDueDate.markAsTouched();
    this.dueDateValidator(this.form.controls.actionPlanDueDate);
  }

  public dueDateValidator(control: AbstractControl): ValidationErrors {
    if ((control.touched || !control.pristine) && !control.value) {
      this.dueDateStatus = {
        state: EnumErrorStates.ERROR,
        text: this.translocoService.translate('create-action-plan-dialog-component.date-required'),
      };
      return { required: this.dueDateStatus.text };
    }

    this.dueDateStatus = { state: EnumErrorStates.SUCCESS };
    return null;
  }

  onSimulatedValueChanged(value: number): void {
    this.simulatedValue = value;
  }

  onStepperModeChanged(mode: Mode): void {
    this.stepperMode = mode;
  }

  public async onCreatePlan() {
    this.isSaving = true;

    this.form.disable();

    const groupId = this.groupService.getSelectedGroupId();
    if (this.actionPlan) {
      this.actionPlan.metric_target_value = this.simulatedValue;
      this.actionPlan.due_date = Utils.getDateIsoString(this.form.controls.actionPlanDueDate.value);
      this.actionPlan.notes = this.form.controls.actionPlanNote.value;

      try {
        const updatedActionPlan = await this.actionPlanService.updateActionPlan(
          groupId,
          this.actionPlan
        );

        this.update.emit(updatedActionPlan);

        this.currentStep = 2; // opens up the success notification
      } catch (error) {
        this.alertService.showErrorToast(
          this.translocoService.translate('create-action-plan-dialog-component.failed-to-update'),
          error
        );
      }
    } else {
      try {
        const project = await this.projectService.getProjectById(this.dashboard.project_id, false);

        const actionPlanToCreate = ActionPlanService.createActionPlanObject({
          calculationId: this.widgetDataSource.id,
          collectionName: this.widgetDataSource.collectionName,
          collectionDisplayName: this.widgetDataSource.displayName,
          driverColumn: this.driver.name,
          driverName: this.driver.fieldLongName,
          breakdown: this.selectedBreakdown,
          breakdownColumn: this.breakdownColumn,
          notes: this.form.controls.actionPlanNote.value,
          dueDate: Utils.getDateIsoString(this.form.controls.actionPlanDueDate.value),
          originalMetric: this.driver.current,
          targetMetric: this.simulatedValue,
          projectId: project.id,
          projectName: project.name,
          dashboardId: this.dashboard.id,
          scoringStrategy: this.scoringStrategy ?? Constants.TWTRA_DEFAULT_SCORING_STRATEGY,
          filters: this.analysisMetadata['dashboard:filters'],
          comparisonFilters: this.analysisMetadata['dashboard:comparisonFilters'],
          widgetGroupFilters: this.analysisMetadata['dashboard:widgetGroupFilters'],
          weightField: this.analysisMetadata.weightField,
        });

        this.actionPlan = await this.actionPlanService.createActionPlan(
          groupId,
          actionPlanToCreate
        );

        this.create.emit(this.actionPlan);

        this.currentStep = 2;
      } catch (error) {
        this.alertService.showErrorToast(
          this.translocoService.translate('create-action-plan-dialog-component.failed-to-create'),
          error
        );
      }
    }

    this.form.enable();

    this.isSaving = false;
  }
}
